import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "./client";
import { useCachingConfig } from "@src/config/caching-config";
import type { AgentRequest, AgentResponse, ArmedResponse, AlarmRoutine, AlarmException, AlarmOverride } from "./types";
import type { AlarmContacts, AlarmConfiguration, EmailSuggestion } from "@visioni/a3s-alarm-schedules/schema";
import type { CameraApiResponse } from "./cameras-api";
import { useTenantId, useTenant } from "@src/features/tenants";
import { isLegacyAppCamera } from "@src/utils/key-parser";
import { isWeb } from "@src/utils/web-helpers";

const MS_IN_SECOND = 1000;

const BULK_CONCURRENCY = 5;

/** Run async tasks with limited concurrency */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      try {
        results[i] = { status: "fulfilled", value: await fn(items[i]) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));
  return results;
}

const buildAlarmDetailKey = (tenantId: string, siteId: string, cameraKey: string) => [
  "alarms",
  tenantId,
  siteId,
  cameraKey,
];

const refreshAlarmQueries = (queryClient: QueryClient, tenantId: string, siteId: string, cameraKey: string) => {
  if (!tenantId || !siteId || !cameraKey) {
    return Promise.resolve();
  }

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: buildAlarmDetailKey(tenantId, siteId, cameraKey), exact: true }),
    queryClient.invalidateQueries({ queryKey: ["alarms", tenantId, siteId], exact: false }),
  ]);
};

export const commonQueryOptions = {
  staleTime: 0,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

export const useAlarmsList = (siteId: string) => {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["alarms", tenantId, siteId],
    queryFn: () => api.alarms.list(tenantId, siteId),
    enabled: !!tenantId && !!siteId,
    ...commonQueryOptions,
  });
};

export const useAlarm = (siteId: string, cameraKey: string | undefined) => {
  const tenantId = useTenantId();
  const cachingConfig = useCachingConfig();
  return useQuery({
    queryKey: ["alarms", tenantId, siteId, cameraKey],
    queryFn: async () => {
      console.log(`[useAlarm] Starting fetch for camera: ${cameraKey}, site: ${siteId}, tenant: ${tenantId}`);
      try {
        const result = await api.alarms.getByCamera(tenantId, siteId, cameraKey!);
        console.log(`[useAlarm] Successfully fetched alarm data for camera: ${cameraKey}`);
        return result;
      } catch (error) {
        console.error(`[useAlarm] Error fetching alarm data for camera: ${cameraKey}`, error);
        throw error;
      }
    },
    enabled: !!tenantId && !!siteId && !!cameraKey,
    staleTime: cachingConfig.alarms.staleTimeMs, // Default 0 (always stale) but configurable via Amplitude
    refetchOnMount: "always", // Refetch when component mounts to ensure fresh data
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1, // Reduce retries to fail faster on timeout/errors
  });
};

export const useAlarmCacheForCameras = (cameras: { cameraKey: string; siteId: string }[]) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMemo(
    () =>
      cameras.map(
        ({ cameraKey, siteId }) =>
          queryClient.getQueryData<AlarmConfiguration>(buildAlarmDetailKey(tenantId, siteId, cameraKey)) ?? null,
      ),
    [cameras, queryClient, tenantId],
  );
};

export const useAlarmsByCameras = (siteId: string, cameraKeys: string[]) => {
  const tenantId = useTenantId();
  return useQueries({
    queries: cameraKeys.map((cameraKey) => ({
      queryKey: buildAlarmDetailKey(tenantId, siteId, cameraKey),
      queryFn: () => api.alarms.getByCamera(tenantId, siteId, cameraKey),
      enabled: !!tenantId && !!siteId,
      staleTime: 5 * 60 * 1000,
    })),
  });
};

export const useUpdateAlarmResponse = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({ siteId, cameraKey, response }: { siteId: string; cameraKey: string; response: ArmedResponse }) =>
      api.alarms.updateResponse(tenantId, siteId, cameraKey, response),
    onSuccess: (data, variables) => {
      // Update the cache directly with the returned data to avoid refetch
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
    },
  });
};

export const useUpdateAlarmRoutine = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      siteId,
      cameraKey,
      routine,
      timezone,
    }: {
      siteId: string;
      cameraKey: string;
      routine: AlarmRoutine;
      timezone: string;
    }) => api.alarms.updateRoutine(tenantId, siteId, cameraKey, routine, timezone),
    onSuccess: (data, variables) => {
      // Update the cache directly with the returned data to avoid refetch
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
    },
  });
};

export const useUpdateAlarmRoutines = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async ({
      siteId,
      cameras,
      routine,
    }: {
      siteId: string;
      cameras: { cameraKey: string; timezone: string }[];
      routine: AlarmRoutine;
    }) => {
      const results = await mapWithConcurrency(cameras, BULK_CONCURRENCY, async ({ cameraKey, timezone }) => {
        const result = await api.alarms.updateRoutine(tenantId, siteId, cameraKey, routine, timezone);
        queryClient.setQueryData(buildAlarmDetailKey(tenantId, siteId, cameraKey), result);
        return result;
      });
      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length > 0) {
        throw new Error(`Failed to update ${failures.length} of ${cameras.length} cameras`);
      }
      return results;
    },
    onSuccess: (_, variables) => {
      // Cache is already fresh from setQueryData calls in mutationFn.
      // Use refetchType 'none' to mark stale without triggering 35 parallel refetches.
      queryClient.invalidateQueries({
        queryKey: ["alarms", tenantId, variables.siteId],
        exact: false,
        refetchType: "none",
      });
    },
  });
};

export const useUpdateAlarmExceptions = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      siteId,
      cameraKey,
      exceptions,
      timezone,
    }: {
      siteId: string;
      cameraKey: string;
      exceptions: AlarmException[];
      timezone: string;
    }) => api.alarms.updateExceptions(tenantId, siteId, cameraKey, exceptions, timezone),
    onSuccess: (data, variables) => {
      // Update the cache directly with the returned data to avoid refetch
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
    },
  });
};

export const useUpdateAlarmExceptionsBulk = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async ({
      siteId,
      cameras,
    }: {
      siteId: string;
      cameras: { cameraKey: string; exceptions: AlarmException[]; timezone: string }[];
    }) => {
      const results = await mapWithConcurrency(
        cameras,
        BULK_CONCURRENCY,
        async ({ cameraKey, exceptions, timezone }) => {
          const result = await api.alarms.updateExceptions(tenantId, siteId, cameraKey, exceptions, timezone);
          queryClient.setQueryData(buildAlarmDetailKey(tenantId, siteId, cameraKey), result);
          return result;
        },
      );
      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length > 0) {
        throw new Error(`Failed to update ${failures.length} of ${cameras.length} cameras`);
      }
      return results;
    },
    onSuccess: (_, variables) => {
      // Cache is already fresh from setQueryData calls in mutationFn.
      // Use refetchType 'none' to mark stale without triggering 35 parallel refetches.
      queryClient.invalidateQueries({
        queryKey: ["alarms", tenantId, variables.siteId],
        exact: false,
        refetchType: "none",
      });
    },
  });
};

export const useUpdateAlarmOverride = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      siteId,
      cameraKey,
      override,
      timezone,
    }: {
      siteId: string;
      cameraKey: string;
      override: AlarmOverride | null;
      timezone: string;
    }) => api.alarms.updateOverride(tenantId, siteId, cameraKey, override, timezone),
    onSuccess: (data, variables) => {
      // Update the cache directly with the returned data to avoid refetch
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
      // Invalidate cameras query so that thumbnails and badges refresh too
      queryClient.invalidateQueries({ queryKey: ["cameras", tenantId], exact: false });
    },
  });
};

export const useUpdateAlarmSettings = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (params: {
      siteId: string;
      cameraKey: string;
      body: {
        response?: ArmedResponse;
        routine?: AlarmRoutine;
        exceptions?: AlarmException[];
        timezone?: string;
      };
    }) => api.alarms.updateSettings(tenantId, params.siteId, params.cameraKey, params.body),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
    },
  });
};

export const useRoutineSuggestions = (siteId: string, cameraKey: string | undefined) => {
  const tenantId = useTenantId();
  console.log("useRoutineSuggestions called with cameraKey:", cameraKey);
  return useQuery({
    queryKey: ["alarms", tenantId, siteId, cameraKey, "routine-suggestions"],
    queryFn: () => {
      console.log("useRoutineSuggestions queryFn executing for cameraKey:", cameraKey);
      return api.alarms.getRoutineSuggestions(tenantId, siteId, cameraKey!);
    },
    enabled: !!tenantId && !!siteId && !!cameraKey,
  });
};

export const useExceptionSuggestions = (siteId: string, cameraKey: string | undefined) => {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["alarms", tenantId, siteId, cameraKey, "exception-suggestions"],
    queryFn: () => api.alarms.getExceptionSuggestions(tenantId, siteId, cameraKey!),
    enabled: !!tenantId && !!siteId && !!cameraKey,
  });
};

export const useAgentQuery = () => {
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      siteId,
      cameraKey,
      request,
    }: {
      siteId: string;
      cameraKey: string;
      request: AgentRequest;
    }): Promise<AgentResponse> => api.alarms.agent.query(tenantId, siteId, cameraKey, request),
  });
};

// Camera hooks
export const useCameras = (siteId?: string) => {
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();
  const cachingConfig = useCachingConfig();

  const query = useQuery<CameraApiResponse[]>({
    queryKey: ["cameras", tenantId, siteId],
    queryFn: () => api.cameras.search(tenantId),
    enabled: !isTenantLoading && !!tenantId,
    select: isWeb ? (data) => data.filter((camera) => !isLegacyAppCamera(camera)) : undefined,
    // Use the minimum snapshot refresh interval from all cameras for staleTime
    // Default to 5 minutes (300 seconds) if no data yet
    staleTime: cachingConfig.cameras.snapTtlSec * MS_IN_SECOND,
    // Refresh thumbnails on the tightest per-camera cadence. refetchInterval runs
    // against the raw (pre-select) data, so read snapshotRefreshInterval from
    // there. Pauses when unfocused (refetchIntervalInBackground defaults false).
    refetchInterval: (query) => {
      const cameras = query.state.data;
      const seconds =
        cameras && cameras.length > 0 ?
          Math.min(...cameras.map((c) => c.snapshotRefreshInterval ?? cachingConfig.cameras.snapTtlSec))
        : cachingConfig.cameras.snapTtlSec;
      return seconds * MS_IN_SECOND;
    },
    // Always refetch on mount to ensure fresh thumbnails
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    ...query,
    isTenantPending: isTenantLoading || !tenantId,
  };
};

export const useCamera = (siteId: string, cameraKey: string) => {
  const tenantId = useTenantId();
  const cachingConfig = useCachingConfig();

  const query = useQuery<CameraApiResponse>({
    queryKey: ["cameras", tenantId, siteId, cameraKey],
    queryFn: () => api.cameras.get(tenantId, siteId, cameraKey!),
    enabled: !!tenantId && !!siteId && !!cameraKey,
    // Use the camera's snapshot refresh interval for staleTime
    // Default to 5 minutes (300 seconds) if not specified
    staleTime: cachingConfig.cameras.snapTtlSec * MS_IN_SECOND,
    // Refresh the snapshot on the camera's cadence. refetchIntervalInBackground
    // defaults to false, so this pauses when the app isn't focused (see the
    // focusManager/onlineManager wiring in query-client.tsx).
    refetchInterval: (query) =>
      (query.state.data?.snapshotRefreshInterval ?? cachingConfig.cameras.snapTtlSec) * MS_IN_SECOND,
    // Always refetch on mount to ensure fresh thumbnails
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Keep showing previous snapshot URL while fetching new one
    placeholderData: (previousData) => previousData,
  });

  return query;
};

export const useUpdateCameraNickname = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({ siteId, cameraKey, nickname }: { siteId: string; cameraKey: string; nickname: string | null }) =>
      api.cameras.updateNickname(tenantId, siteId, cameraKey, nickname),
    onSuccess: (data, variables) => {
      // Update the camera cache with the new nickname
      queryClient.setQueryData(["cameras", tenantId, variables.siteId, variables.cameraKey], (old: any) => {
        if (old) {
          return { ...old, nickname: data.nickname };
        }
        return old;
      });
      // Invalidate the cameras list to ensure it's fresh
      queryClient.invalidateQueries({ queryKey: ["cameras", tenantId], exact: false });
    },
  });
};

// Silence alarm hook
export const useUpdateAlarmTimezone = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({ siteId, cameraKey, timezone }: { siteId: string; cameraKey: string; timezone: string }) =>
      api.alarms.updateTimezone(tenantId, siteId, cameraKey, timezone),
    onSuccess: (data, variables) => {
      // Update the cache directly with the returned data to avoid refetch
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
    },
  });
};

export const useUpdateAlarmSilence = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({
      siteId,
      cameraKey,
      silence,
      timezone,
    }: {
      siteId: string;
      cameraKey: string;
      silence: { id: string; reason?: string; period: { start: unknown; end: unknown } } | null;
      timezone: string;
    }) => api.alarms.updateSilence(tenantId, siteId, cameraKey, silence, timezone),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["alarms", tenantId, variables.siteId, variables.cameraKey], data);
      refreshAlarmQueries(queryClient, tenantId, variables.siteId, variables.cameraKey);
    },
  });
};

// Camera contacts hooks
export const useCameraContacts = (siteId: string | undefined, cameraKey: string | undefined) => {
  const tenantId = useTenantId();
  return useQuery<AlarmContacts>({
    queryKey: ["camera-contacts", tenantId, siteId, cameraKey],
    queryFn: () => api.contacts.getCameraContacts(tenantId, siteId!, cameraKey!),
    enabled: !!tenantId && !!siteId && !!cameraKey,
  });
};

export const useUpdateCameraContacts = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({ siteId, cameraKey, contacts }: { siteId: string; cameraKey: string; contacts: AlarmContacts }) =>
      api.contacts.updateCameraContacts(tenantId, siteId, cameraKey, contacts),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        ["camera-contacts", tenantId, variables.siteId, variables.cameraKey],
        variables.contacts,
      );
    },
  });
};

export const useCameraEmailSuggestions = (
  siteId: string | undefined,
  cameraKey: string | undefined,
  query?: string,
  enabled: boolean = true,
) => {
  const tenantId = useTenantId();
  return useQuery<EmailSuggestion[]>({
    queryKey: ["camera-contacts", "email-suggestions", tenantId, siteId, cameraKey, query],
    queryFn: () => api.contacts.getEmailSuggestions(tenantId, siteId!, cameraKey!, query),
    enabled: enabled && !!tenantId && !!siteId && !!cameraKey,
  });
};
